/** One price update as the tick chart keeps it: server time in ms, bid and ask. */
export interface Tick {
  t: number;
  bid: number;
  ask: number;
}

/** How many updates the tick chart keeps (the newest win). */
export const MAX_TICKS = 2000;

/** Adds an update unless it repeats the last one (the same moment and prices), keeping at most `max`. */
export function appendTick(list: readonly Tick[], tick: Tick, max = MAX_TICKS): Tick[] {
  const last = list[list.length - 1];
  if (last && last.t === tick.t && last.bid === tick.bid && last.ask === tick.ask) return list as Tick[];
  if (last && tick.t < last.t) return list as Tick[]; // an older update never goes after a newer one
  const next = [...list, tick];
  return next.length > max ? next.slice(next.length - max) : next;
}

/**
 * The tick chart's drawing: polylines for bid and ask across `width`, scaled to the prices shown, with a margin.
 * `lo`/`hi` are the padded price range the lines are actually scaled against (unlike `min`/`max`, the true extremes),
 * so a caller can place grid lines, axis labels or a price tag at the same y a point on the line would be.
 */
export function tickPaths(
  ticks: readonly Tick[],
  width: number,
  height: number,
): {
  bid: string;
  ask: string;
  min: number;
  max: number;
  lo: number;
  hi: number;
  y: (price: number) => number;
} | null {
  if (ticks.length === 0 || width <= 0 || height <= 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const t of ticks) {
    min = Math.min(min, t.bid, t.ask);
    max = Math.max(max, t.bid, t.ask);
  }
  const pad = (max - min || Math.abs(max) * 0.0001 || 1) * 0.1;
  const lo = min - pad;
  const hi = max + pad;
  const x = (i: number) => (ticks.length === 1 ? width : (i / (ticks.length - 1)) * width);
  const y = (p: number) => height - ((p - lo) / (hi - lo)) * height;
  const path = (key: 'bid' | 'ask') =>
    ticks.map((t, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(t[key]).toFixed(1)}`).join(' ');
  return { bid: path('bid'), ask: path('ask'), min, max, lo, hi, y };
}

/** A handful of evenly spaced price levels between `lo` and `hi`, for the axis and the grid lines, newest-looking first. */
export function gridLevels(lo: number, hi: number, count = 5): number[] {
  if (!(hi > lo) || count < 2) return [];
  const step = (hi - lo) / (count - 1);
  return Array.from({ length: count }, (_, i) => hi - i * step);
}
